import type React from "react"

import { Suspense, useState } from "react"
import { DashboardShell } from "@/components/dashboard-shell"
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FileText, Calendar, Download, Filter, Search, Eye } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"

const reports = [
  {
    id: 1,
    title: "Q4 2025 Renewable Energy Market Analysis",
    description:
      "Comprehensive quarterly analysis covering market trends, investment flows, and policy developments across UK renewable energy sector.",
    category: "Market Analysis",
    date: "2025-12-20",
    author: "Research Team",
    pages: 45,
    downloads: 234,
    status: "Published",
  },
  {
    id: 2,
    title: "Scottish Offshore Wind Pipeline Report",
    description:
      "Deep dive into Scotland's offshore wind development pipeline, featuring 15 projects totaling 2.5GW capacity with detailed Gate 2 analysis.",
    category: "Regional Report",
    date: "2025-11-15",
    author: "Regional Team",
    pages: 62,
    downloads: 189,
    status: "Published",
  },
  {
    id: 3,
    title: "Technology Innovation in Solar Energy",
    description:
      "Analysis of emerging solar technologies including bifacial panels, floating solar, and advanced tracking systems with ROI projections.",
    category: "Technology Report",
    date: "2025-10-28",
    author: "Tech Team",
    pages: 38,
    downloads: 156,
    status: "Published",
  },
  {
    id: 4,
    title: "Energy Storage Investment Outlook 2026",
    description:
      "Forward-looking analysis of battery storage market opportunities, policy incentives, and project economics across UK regions.",
    category: "Investment Report",
    date: "2025-12-05",
    author: "Investment Team",
    pages: 51,
    downloads: 312,
    status: "Published",
  },
  {
    id: 5,
    title: "Gate 2 Methodology Update v2.5",
    description:
      "Updated assessment framework incorporating new regulatory requirements and enhanced ESG scoring criteria for project evaluation.",
    category: "Methodology",
    date: "2025-09-10",
    author: "Analytics Team",
    pages: 28,
    downloads: 445,
    status: "Published",
  },
  {
    id: 6,
    title: "H1 2026 Deal Flow Preview",
    description:
      "Early preview of expected renewable energy transactions and investment opportunities for first half 2026 with deal sizing analysis.",
    category: "Market Analysis",
    date: "2025-12-15",
    author: "M&A Team",
    pages: 33,
    downloads: 87,
    status: "Draft",
  },
]

function ReportsContent() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [open, setOpen] = useState(false)

  const handleGenerateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsGenerating(true)

    // Simulate report generation
    await new Promise((resolve) => setTimeout(resolve, 2000))

    setIsGenerating(false)
    setOpen(false)
    // You could add toast notification here
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">Access market research and analysis reports</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <FileText className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <form onSubmit={handleGenerateReport}>
              <DialogHeader>
                <DialogTitle>Generate New Report</DialogTitle>
                <DialogDescription>
                  Configure and generate a custom report based on your selected criteria
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-6 py-6">
                <div className="grid gap-2">
                  <Label htmlFor="report-type">Report Type</Label>
                  <Select name="report-type" defaultValue="market">
                    <SelectTrigger>
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="market">Market Analysis</SelectItem>
                      <SelectItem value="regional">Regional Report</SelectItem>
                      <SelectItem value="technology">Technology Report</SelectItem>
                      <SelectItem value="investment">Investment Report</SelectItem>
                      <SelectItem value="portfolio">Portfolio Overview</SelectItem>
                      <SelectItem value="gate2">Gate 2 Summary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="title">Report Title</Label>
                  <Input id="title" name="title" placeholder="e.g. Q1 2026 Solar Energy Analysis" required />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Brief description of the report scope and objectives..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="date-from">Date Range From</Label>
                    <Input id="date-from" name="date-from" type="date" defaultValue="2025-01-01" />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="date-to">Date Range To</Label>
                    <Input id="date-to" name="date-to" type="date" defaultValue="2025-12-31" />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Include Sections</Label>
                  <div className="grid gap-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="executive" defaultChecked />
                      <Label htmlFor="executive" className="font-normal cursor-pointer">
                        Executive Summary
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="financial" defaultChecked />
                      <Label htmlFor="financial" className="font-normal cursor-pointer">
                        Financial Analysis
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="gate2-section" defaultChecked />
                      <Label htmlFor="gate2-section" className="font-normal cursor-pointer">
                        Gate 2 Assessment
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="market-trends" />
                      <Label htmlFor="market-trends" className="font-normal cursor-pointer">
                        Market Trends
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="recommendations" />
                      <Label htmlFor="recommendations" className="font-normal cursor-pointer">
                        Recommendations
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="regions">Regions</Label>
                  <Select name="regions" defaultValue="all">
                    <SelectTrigger>
                      <SelectValue placeholder="Select regions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      <SelectItem value="scotland">Scotland</SelectItem>
                      <SelectItem value="england">England</SelectItem>
                      <SelectItem value="wales">Wales</SelectItem>
                      <SelectItem value="ni">Northern Ireland</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="format">Output Format</Label>
                  <Select name="format" defaultValue="pdf">
                    <SelectTrigger>
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      <SelectItem value="ppt">PowerPoint Presentation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isGenerating}>
                  {isGenerating ? "Generating..." : "Generate Report"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search reports..." className="pl-10" />
        </div>
        <Select defaultValue="all">
          <SelectTrigger className="w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="market">Market Analysis</SelectItem>
            <SelectItem value="regional">Regional Report</SelectItem>
            <SelectItem value="technology">Technology Report</SelectItem>
            <SelectItem value="investment">Investment Report</SelectItem>
            <SelectItem value="methodology">Methodology</SelectItem>
          </SelectContent>
        </Select>
        <Select defaultValue="newest">
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="popular">Most Popular</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4">
        {reports.map((report) => (
          <Card key={report.id} className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={report.status === "Published" ? "default" : "secondary"}>{report.category}</Badge>
                    <Badge variant={report.status === "Published" ? "outline" : "secondary"}>{report.status}</Badge>
                  </div>
                  <CardTitle className="text-xl mb-2">{report.title}</CardTitle>
                  <CardDescription>{report.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    {new Date(report.date).toLocaleDateString("en-GB")}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <FileText className="h-4 w-4" />
                    {report.pages} pages
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Download className="h-4 w-4" />
                    {report.downloads} downloads
                  </div>
                  <div>By {report.author}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  )
}

export default function ReportsPage() {
  return (
    <DashboardShell>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Reports</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 p-6">
          <Suspense fallback={null}>
            <ReportsContent />
          </Suspense>
        </div>
      </DashboardShell>
  )
}
